import React, { useState } from 'react';

import { useFormikContext } from 'formik';
import { mutate } from 'swr';

import ChartTitleField from '@components/experiments/plotDisplay/fields/ChartTitleField';
import FullWidthToggleField from '@components/experiments/plotDisplay/fields/FullWidthToggleField';
import ThemeFieldGroup from '@components/experiments/plotDisplay/groups/ThemeFieldGroup';

import { useExperimentAnnotationContext } from '@contexts/ExperimentAnnotationContext';

import { AnalysisParameters, SeuratDifferentialExpressionAnalysisParameters } from '@models/AnalysisParameters';
import Experiment from '@models/Experiment';
import Plot from '@models/Plot';
import { RidgePlotDisplayOptionFormValues } from '@models/PlotDisplayOption';

import Endpoints from '@services/Endpoints';

import CellGroups from '../fields/CellGroups';
import AssaySummaryLegendFieldGroupV3 from '../groups/AssaySummaryLegendFieldGroupV3';

type Props = {
    analysisParameters?: AnalysisParameters | null;
    experiment: Experiment;
    plot: Plot;
    setDisplaySaveDisabled: (value: boolean) => void;
    setUpdateGroupsDisabled: (value: boolean) => void;
    updateGroupsDisabled: boolean;
};
const RidgePlotDisplayFields = ({
    analysisParameters,
    experiment,
    plot,
    setDisplaySaveDisabled,
    setUpdateGroupsDisabled,
    updateGroupsDisabled,
}: Props) => {
    const { values, setFieldValue } = useFormikContext<RidgePlotDisplayOptionFormValues>();
    const [splitByCluster, setSplitByCluster] = useState(Boolean(values?.annotation_set_id) ?? false);
    const { annotationSets } = useExperimentAnnotationContext();
    const typedParameters = analysisParameters as SeuratDifferentialExpressionAnalysisParameters;
    const isCleanedWorkflow = experiment?.is_cleaned_workflow ?? false;

    return (
        <>
            <FullWidthToggleField />
            <ChartTitleField placeholder={plot.analysis?.name} />
            <CellGroups
                experiment={experiment}
                plot={plot}
                analysisParameters={typedParameters}
                annotationSets={annotationSets}
                isCleanedWorkflow={isCleanedWorkflow}
            >
                <CellGroups.Header />
                <CellGroups.Description />
                <CellGroups.GroupByClusterField
                    checked={splitByCluster}
                    onChange={(e) => {
                        setSplitByCluster((prev) => !prev);
                        if (!e.target.checked) {
                            setFieldValue('annotation_set_id', null);
                        }
                    }}
                />
                {splitByCluster && (
                    <CellGroups.AnnotationSetField
                        value={values.annotation_set_id}
                        onChange={(e) => setFieldValue('annotation_set_id', e.target.value)}
                    />
                )}
                <CellGroups.VariablesField />
                <CellGroups.LatentVariablesField />
                <CellGroups.SubmitButton
                    disabled={updateGroupsDisabled}
                    onClick={() => {
                        mutate(
                            Endpoints.lab.experiment.plot.groupsV3({
                                experimentId: experiment.uuid,
                                plotId: plot.uuid,
                            }),
                        );
                    }}
                />
            </CellGroups>
            <ThemeFieldGroup hideStyle />
            <AssaySummaryLegendFieldGroupV3
                description="Click and drag the legend items to reorder. Plot will only show groups included in the analysis"
                experiment={experiment}
                ignoreEmpty
                isSortable={true}
                plot={plot}
                setDisplaySaveDisabled={setDisplaySaveDisabled}
                setUpdateGroupsDisabled={setUpdateGroupsDisabled}
            />
        </>
    );
};

export default RidgePlotDisplayFields;
